import { Alert, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';
import { formatDateTime } from '@/utils/locale';
import { useAcknowledgeDropoff, type WorkOrderDropoff } from '@/hooks/useWorkOrders';
import { useStaffUsers } from '@/hooks/useStaffUsers';

const DROPOFF_ACKNOWLEDGE_PERMISSION = 'scheduling.exceptions';

export interface DropoffBannerProps {
  workOrderId: string;
  dropoff: WorkOrderDropoff;
}

function staffDisplayName(
  user: { firstName: string; lastName: string } | undefined,
): string {
  if (!user) return '';
  return `${user.firstName} ${user.lastName}`.trim();
}

export function DropoffBanner({ workOrderId, dropoff }: DropoffBannerProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const currentUser = useAuthStore((s) => s.user);
  const canAcknowledge = hasPermission(DROPOFF_ACKNOWLEDGE_PERMISSION);
  const acknowledge = useAcknowledgeDropoff();
  const { data: staffData } = useStaffUsers();

  const keys = dropoff.keysLocation?.trim() ? dropoff.keysLocation : '—';
  const acknowledged = Boolean(dropoff.acknowledgedAt);
  const acknowledger =
    staffData?.users.find((u) => u.id === dropoff.acknowledgedBy) ??
    (currentUser?.id === dropoff.acknowledgedBy ? currentUser : undefined);
  const acknowledgerName = staffDisplayName(acknowledger);

  const handleAcknowledge = async () => {
    try {
      await acknowledge.mutateAsync(workOrderId);
    } catch {
      notify.error(tc('error'), t('dropoff.acknowledgeFailed'));
    }
  };

  return (
    <Alert
      severity="info"
      sx={{ mb: 1 }}
      data-testid="dropoff-banner"
      action={
        canAcknowledge && !acknowledged ? (
          <Button
            color="inherit"
            size="small"
            onClick={() => { void handleAcknowledge(); }}
            disabled={acknowledge.isPending}
          >
            {t('dropoff.acknowledge')}
          </Button>
        ) : undefined
      }
    >
      <Box>
        <Typography variant="body2">
          {t('dropoff.reported', {
            time: formatDateTime(dropoff.reportedAt),
            keys,
          })}
        </Typography>
        {dropoff.autoArrived ? (
          <Typography variant="body2">
            {t('dropoff.autoArrived')}
          </Typography>
        ) : null}
        {acknowledged ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {acknowledgerName
              ? t('dropoff.acknowledged', {
                  name: acknowledgerName,
                  time: formatDateTime(dropoff.acknowledgedAt),
                })
              : t('dropoff.acknowledgedAt', {
                  time: formatDateTime(dropoff.acknowledgedAt),
                })}
          </Typography>
        ) : null}
      </Box>
    </Alert>
  );
}
